import { Link } from "react-router-dom";
import { ArrowRight, MessageSquare, FileText, Cuboid, Hammer, KeyRound } from "lucide-react";
import Reveal from "@/components/Reveal";

const steps = [
    {
        num: "01",
        title: "Consultation",
        duration: "Week 0",
        icon: MessageSquare,
        desc: "We meet at your space or our Jubilee Hills studio. Understand how you live, your aesthetic, the way your family flows through a day. Define ambition and budget — together.",
        deliverables: ["Site visit & measurement", "Lifestyle questionnaire", "Reference mood-boards", "Indicative budget"],
    },
    {
        num: "02",
        title: "Design Brief",
        duration: "Week 1 – 3",
        icon: FileText,
        desc: "A detailed signed-off brief — palette, finish, furniture, and material palette. We lock direction before a single line is drawn so there are no expensive course-corrections later.",
        deliverables: ["Detailed brief document", "Material & palette boards", "Concept floor plans", "Itemised quote"],
    },
    {
        num: "03",
        title: "3D Visualisation",
        duration: "Week 3 – 5",
        icon: Cuboid,
        desc: "Photo-real 3D renders of every room. Iterate, refine, sign-off. You see exactly what you're going to live in — before we mobilise a single carpenter.",
        deliverables: ["Photo-real 3D renders", "VR walkthrough (optional)", "Working drawings", "Final BOQ"],
    },
    {
        num: "04",
        title: "Execution",
        duration: "Week 6 – 16",
        icon: Hammer,
        desc: "Workshop-built joinery, vendor management, daily quality checks. A dedicated design manager owns your site. You get weekly walkthroughs, photographs, and budget updates.",
        deliverables: ["Workshop fabrication", "Site execution & MEP", "Weekly walkthroughs", "Daily quality control"],
    },
    {
        num: "05",
        title: "Handover",
        duration: "Week 16 – 18",
        icon: KeyRound,
        desc: "Snag-list, finishing touches, styling and a snag-free handover. We hand over a fully styled home — art, accessories, linen, the works. Plus a 5-year structural warranty on all custom work.",
        deliverables: ["Snag-list resolution", "Final styling & art", "Handover & training", "5-year warranty"],
    },
];

export default function Process() {
    return (
        <div data-testid="process-page" className="bg-charcoal text-cream">
            <section className="container-pad mx-auto pt-40 pb-20 md:pt-48 md:pb-24 border-b border-[#262626]">
                <Reveal>
                    <p className="overline mb-8">— Process</p>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tighter text-cream max-w-5xl">
                        Five steps.<br />
                        <em className="italic text-gold">Zero surprises.</em>
                    </h1>
                    <p className="mt-8 max-w-2xl text-base md:text-lg text-[#A3A3A3]">
                        Our process is intentionally transparent. From your first studio visit to
                        the final art piece, you know exactly what's happening, what it costs, and
                        when it lands.
                    </p>
                </Reveal>
            </section>

            <section className="container-pad mx-auto py-20 md:py-28">
                <div className="relative">
                    {/* Vertical timeline */}
                    <div className="hidden md:block absolute left-[60px] top-0 bottom-0 w-px bg-[#262626]" />

                    <div className="space-y-20 md:space-y-28">
                        {steps.map((step, i) => {
                            const Icon = step.icon;
                            return (
                                <Reveal key={step.num}>
                                    <div data-testid={`process-detail-${i}`} className="grid grid-cols-12 gap-6 md:gap-12 items-start">
                                        <div className="col-span-12 md:col-span-2 flex items-start md:flex-col md:items-start gap-4 relative">
                                            <div className="relative z-10 w-[60px] h-[60px] bg-charcoal border border-gold flex items-center justify-center">
                                                <Icon className="text-gold" size={22} />
                                            </div>
                                            <span className="section-number text-4xl md:text-6xl md:mt-4">{step.num}</span>
                                        </div>
                                        <div className="col-span-12 md:col-span-10 md:pl-6">
                                            <p className="overline mb-3">{step.duration}</p>
                                            <h2 className="font-display text-3xl md:text-5xl tracking-tight text-cream mb-6 leading-tight">
                                                {step.title}
                                            </h2>
                                            <p className="text-[#D8D8D2] text-base md:text-lg leading-relaxed max-w-2xl mb-8">
                                                {step.desc}
                                            </p>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
                                                {step.deliverables.map((d) => (
                                                    <div key={d} className="border-t border-[#262626] pt-4">
                                                        <p className="text-cream text-sm">{d}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="cta-banner border-t border-[#262626] py-24 md:py-32">
                <div className="container-pad mx-auto text-center">
                    <Reveal>
                        <h2 className="font-display text-4xl md:text-6xl text-cream max-w-3xl mx-auto leading-tight tracking-tight">
                            Begin the journey.
                        </h2>
                        <Link to="/contact" className="btn-primary mt-10" data-testid="process-cta">
                            Book Your Consultation <ArrowRight size={16} />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
